import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import {
  enchants,
  gemInfo,
} from 'shared/items';

import WowIcon, { sizeMap, itemBadge } from './WowIcon';

const styles = {
  wrap: {
    display: 'flex',
    padding: '2px 4px 2px 4px',
    backgroundColor: '#222',
    borderRadius: 10,
  },
};

const WowItemIcon = ({
  item,
  classId,
  specId,
  setPieceIds,
  showAzeritePowers,
  slot,
  showGems,
  hideBadge,
  ...props
}) => {
  const sizePx = sizeMap[props.size] || props.size;
  if (!item) {
    const emptySlotIcons = {
      head: 'Head',
      neck: 'Neck',
      shoulder: 'Shoulder',
      back: 'Chest',
      chest: 'Chest',
      shirt: 'Shirt',
      tabard: 'Tabard',
      wrist: 'Wrists',
      hands: 'Hands',
      waist: 'Waist',
      legs: 'Legs',
      feet: 'Feet',
      finger: 'Finger',
      finger1: 'Finger',
      finger2: 'Finger',
      trinket: 'Trinket',
      trinket1: 'Trinket',
      trinket2: 'Trinket',
      main_hand: 'MainHand',
      off_hand: 'SecondaryHand',
    };
    return (
      <img
        src={`/images/paperdoll/UI-PaperDoll-Slot-${emptySlotIcons[slot]}.png`}
        alt={`Empty slot for ${slot}`}
        width={sizePx}
        height={sizePx}
      />
    );
  }

  // bonus lists
  let bonuses;
  if (item.bonus_id) {
    bonuses = item.bonus_id.split('/');
  } else if (item.bonusLists) {
    bonuses = item.bonusLists;
  }
  bonuses = _.map(bonuses, b => parseInt(b, 10));

  // find enchant
  let ench;
  if (item.tooltipParams && item.tooltipParams.enchant) {
    ench = item.tooltipParams.enchant;
  } else if (item.tooltipParams && item.tooltipParams.tinker) {
    ench = item.tooltipParams.tinker;
  } else if (item.enchant_id) {
    ench = item.enchant_id;
  } else if (item.itemEnchant) {
    ench = _.get(_.find(enchants, { key: item.itemEnchant }), 'id');
  }

  // find gems
  let gemStr;
  let gems;
  if (item.gem_id) {
    gems = item.gem_id.split('/');
  } else if (item.tooltipParams) {
    gems = [
      item.tooltipParams.gem0,
      item.tooltipParams.gem1,
      item.tooltipParams.gem2,
    ];
  } else if (item.itemGem0 || item.itemGem1 || item.itemGem2) {
    if (Number.isNaN(Number(item.itemGem0))) {
      gems = [
        _.get(_.find(gemInfo, { shortName: item.itemGem0 }), 'id'),
        _.get(_.find(gemInfo, { shortName: item.itemGem1 }), 'id'),
        _.get(_.find(gemInfo, { shortName: item.itemGem2 }), 'id'),
      ];
    } else {
      gems = [
        item.itemGem0,
        item.itemGem1,
        item.itemGem2,
      ];
    }
  }
  if (gems) {
    const filledGems = _.filter(gems);
    if (filledGems.length > 0) {
      gemStr = filledGems.join(',');
    }
  }

  let craftedStats;
  if (_.isArray(item.craftedStats)) {
    craftedStats = _.join(item.craftedStats, ':');
  } else if (item.craftedStats) {
    craftedStats = item.craftedStats;
  } else if (item.crafted_stats) {
    craftedStats = _.join(_.split(item.crafted_stats, '/'), ':');
  }

  let itemPowers = [];
  if (_.size(_.get(item, 'azeriteEmpoweredItem.azeritePowers')) > 0) {
    // armory data
    if (_.isInteger(_.get(item, 'azeriteEmpoweredItem.azeritePowers[0]'))) {
      itemPowers = item.azeriteEmpoweredItem.azeritePowers;
    } else {
      itemPowers = _.reverse(_.map(item.azeriteEmpoweredItem.azeritePowers, 'id'));
    }
  } else if (
    item.azeritePower0
    || item.azeritePower1
    || item.azeritePower2
    || item.azeritePower3
    || item.azeritePower4
  ) {
    // raidbots gearset
    itemPowers = [
      _.get(item, 'azeritePower0.powerId'),
      _.get(item, 'azeritePower1.powerId'),
      _.get(item, 'azeritePower2.powerId'),
      _.get(item, 'azeritePower3.powerId'),
      _.get(item, 'azeritePower4.powerId'),
    ];
  } else if (item.azerite_powers) {
    // simc input
    itemPowers = item.azerite_powers.split('/');
  } else if (item.tooltipParams) {
    // armory
    itemPowers = [
      item.tooltipParams.azeritePower0,
      item.tooltipParams.azeritePower1,
      item.tooltipParams.azeritePower2,
      item.tooltipParams.azeritePower3,
      item.tooltipParams.azeritePower4,
    ];
  }
  itemPowers = _.map(_.filter(itemPowers), i => parseInt(i));

  let azeritePowers;
  if (itemPowers.length > 0) {
    azeritePowers = [classId, 0, ...itemPowers].join(':');
  }

  const sourceIsChoice = _.includes([
    'custom-item',
    'weekly_reward',
    'token',
  ], item.source);

  let itemIcon = (
    <WowIcon
      icon={item.icon}
      quality={item.quality}
      id={item.id}
      type="item"
      item={item}
      extra={{
        bonus: _.join(bonuses, ':'),
        ench,
        gems: gemStr,
        setPieces: setPieceIds,
        ilvl: item.itemLevel,
        // eslint-disable-next-line
        azerite: azeritePowers,
        spec: specId,
        'crafted-stats': craftedStats,
      }}
      showAzeriteBorder={!!item.azeritePowers}
      borderStyle={sourceIsChoice ? 'dotted' : 'solid'}
      {...props}
    />
  );

  let gemOverlay;
  if (showGems && _.size(gems) > 0) {
    const gemSize = 12;
    gemOverlay = (
      <div
        style={{
          position: 'absolute',
          top: -4,
          right: -4,
          height: gemSize,
          opacity: props.dull ? 0.4 : undefined,
          filter: props.dull ? 'grayscale(25%)' : undefined,
          boxShadow: '-2px 4px 4px black',
        }}
      >
        {_.map(gems, (gemId, i) => {
          const gem = _.find(gemInfo, { id: parseInt(gemId, 10) });
          if (gem) {
            return (
              <WowIcon
                key={`${i}-${gemId}`}
                size={gemSize}
                id={gem.id}
                type="item"
                icon={gem.icon}
                pl={1}
              />
            );
          }
          return null;
        })}
      </div>
    );
  }

  if (gemOverlay) {
    itemIcon = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          lineHeight: 1,
          position: 'relative',
        }}
      >
        {itemIcon}
        {gemOverlay}
      </div>
    );
  }

  let badgeOverlay;
  const badge = itemBadge(item);
  if (!hideBadge && badge) {
    badgeOverlay = (
      <div
        style={{
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
          cursor: 'pointer',
        }}
        title={badge.title}
      >
        <div
          style={{
            background: badge.color,
            textAlign: 'center',
            fontFamily: 'Tahoma',
            fontSize: _.max([sizePx / 4, 8]),
            position: 'relative',
            padding: 1,
          }}
        >
          {badge.text}
        </div>
      </div>
    );
  }
  if (badgeOverlay) {
    itemIcon = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          lineHeight: 1,
          position: 'relative',
        }}
      >
        {itemIcon}
        {badgeOverlay}
      </div>
    );
  }

  if (showAzeritePowers && item.azeritePowers) {
    const margin = 2;
    return (
      <div
        style={styles.wrap}
      >
        <div style={{ margin, marginRight: itemPowers.length > 0 ? 8 : margin }}>
          {itemIcon}
        </div>
        {itemPowers.map(powerId => {
          const power = _.find(item.azeritePowers, { powerId });
          if (!power) {
            return null;
          }
          return (
            <div style={{ margin }} key={powerId}>
              <WowIcon
                type="spell"
                id={power.spellId}
                icon={power.icon}
                extra={{ ilvl: item.itemLevel }}
                {...props}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return itemIcon;
};

WowItemIcon.propTypes = {
  // eslint-disable-next-line
  item: PropTypes.object,
  classId: PropTypes.number,
  showAzeritePowers: PropTypes.bool,
  // eslint-disable-next-line
  setPieceIds: PropTypes.array,
  showGems: PropTypes.bool,
  hideBadge: PropTypes.bool,
  size: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

WowItemIcon.defaultProps = {
  item: null,
  classId: null,
  setPieceIds: null,
  showAzeritePowers: false,
  showGems: false,
  hideBadge: false,
  size: 'medium',
};

export default WowItemIcon;
